import { KeyboardEvent, MouseEvent, TransitionEvent, useState } from 'react';
import { CriticalSystem } from '@/types/CriticalInfo';
import { useAutoFocusFirstField } from '@/hooks/useAutoFocusFirstField';
import {
	canRemoveSystem,
	getSystemInfoByName,
	getSystemInstanceLabel,
	getSystemNotesSummary,
	getSystemRowLabel,
	hasSystemNotes,
	isSystemDocumented,
} from '@/utils/criticalInfoUtils';

type CloseAction = 'cancel' | 'finish';
type FormMode = 'row' | 'primary' | 'add';

interface UseSystemGroupCardStateProps {
	categoryName: string;
	systems: CriticalSystem[];
	isFilled: boolean;
	expandedSystem: string | null;
	addingSystemName?: string | null;
	onToggleSystem: (systemName: string | null) => void;
	onStartAdding: () => void;
	onFinishAdding?: () => void;
	onCancelEdit: (systemName: string) => void;
	onDoneSystem?: (systemName: string) => void;
	onRemoveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	onUpdateField: (systemName: string, fieldName: string, value: string) => void;
	dirtySystemNames?: Set<string>;
	savingSystemName?: string | null;
	isSaving?: boolean;
	onSaveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	collapseCancelsSystemNames?: Set<string>;
	canEdit?: boolean;
	canRemove?: boolean;
}

export const useSystemGroupCardState = (props: UseSystemGroupCardStateProps) => {
	const {
		categoryName,
		systems,
		isFilled,
		expandedSystem,
		addingSystemName,
		onToggleSystem,
		onStartAdding,
		onFinishAdding,
		onCancelEdit,
		onDoneSystem,
		onRemoveSystem,
		onUpdateField,
		dirtySystemNames,
		savingSystemName,
		isSaving = false,
		onSaveSystem,
		collapseCancelsSystemNames,
		canEdit = true,
		canRemove = true,
	} = props;

	const [pendingAddCloseAction, setPendingAddCloseAction] = useState<CloseAction | null>(null);
	const [pendingPrimaryDoneSystemName, setPendingPrimaryDoneSystemName] = useState<string | null>(null);

	const primarySystem = systems[0];
	const primarySystemInfo = primarySystem ? getSystemInfoByName(primarySystem.name) : null;
	const addingSystem = systems.find((system) => system.name === addingSystemName);
	const isCompletingPrimary = !!pendingPrimaryDoneSystemName;
	const isHeaderFilled = isFilled || isCompletingPrimary;
	const isPrimaryFormOpen =
		!!primarySystem && !isFilled && expandedSystem === primarySystem.name && !isCompletingPrimary;
	const showEmptyHeaderLine = !isHeaderFilled && !isPrimaryFormOpen;
	const showPrimaryTooltipLine = isPrimaryFormOpen && !!primarySystemInfo?.tooltip;
	const isAddFormOpen = !!addingSystem && !pendingAddCloseAction;
	// The primary block carries the empty slot / form / completing-morph. Once the
	// category is filled the primary system is just a normal row, so drop the block
	// (and its mounted form) — but keep it through the close/complete transitions.
	const showPrimaryBlock = !!primarySystem && (!isFilled || isCompletingPrimary);

	const visibleSystems = systems.filter(
		(system) =>
			isFilled &&
			system.name !== pendingPrimaryDoneSystemName &&
			(system !== addingSystem || pendingAddCloseAction === 'finish'),
	);
	const documentedCount = visibleSystems.filter(isSystemDocumented).length;
	const addFormRef = useAutoFocusFirstField<HTMLDivElement>(isAddFormOpen);
	const primaryFormRef = useAutoFocusFirstField<HTMLDivElement>(isPrimaryFormOpen);
	const showRemoveEmptySlot =
		!!primarySystem &&
		canRemove &&
		!isFilled &&
		!isPrimaryFormOpen &&
		canRemoveSystem(primarySystem) &&
		(!!onRemoveSystem || collapseCancelsSystemNames?.has(primarySystem.name));

	const finishPendingAddClose = () => {
		if (!addingSystem || !pendingAddCloseAction) return;

		if (pendingAddCloseAction === 'cancel') onCancelEdit(addingSystem.name);
		onFinishAdding?.();
		setPendingAddCloseAction(null);
	};

	const finishPendingPrimaryDone = () => {
		if (!pendingPrimaryDoneSystemName) return;

		onDoneSystem?.(pendingPrimaryDoneSystemName);
		onToggleSystem(null);
		setPendingPrimaryDoneSystemName(null);
	};

	const handleCancelForm = (systemName: string) => {
		if (!canEdit) return;
		if (systemName === addingSystemName) {
			setPendingAddCloseAction('cancel');
			return;
		}

		onCancelEdit(systemName);
		onToggleSystem(null);
	};

	const handleSaveForm = async (systemName: string) => {
		if (!canEdit || !onSaveSystem) return;
		const isAddTarget = systemName === addingSystemName;

		const saved = await onSaveSystem(systemName);
		if (saved === false) return;

		if (isAddTarget) setPendingAddCloseAction('finish');
		else onToggleSystem(null);
	};

	const handleDoneForm = (systemName: string) => {
		if (!canEdit) return;
		if (!isFilled && systemName === primarySystem?.name) {
			if (!isSystemDocumented(primarySystem)) {
				if (collapseCancelsSystemNames?.has(systemName)) onCancelEdit(systemName);
				onToggleSystem(null);
				return;
			}
			setPendingPrimaryDoneSystemName(systemName);
			return;
		}
		if (systemName === addingSystemName) {
			setPendingAddCloseAction(addingSystem && isSystemDocumented(addingSystem) ? 'finish' : 'cancel');
			return;
		}
		const system = systems.find((candidate) => candidate.name === systemName);
		if (system && isSystemDocumented(system)) onDoneSystem?.(systemName);
		onToggleSystem(null);
	};

	const handleRemoveSystem = async (systemName: string) => {
		if (!canRemove || !onRemoveSystem) return;
		const removed = await onRemoveSystem(systemName);
		if (removed !== false) onToggleSystem(null);
	};

	const handleRemoveEmptySlot = () => {
		if (!primarySystem) return;
		if (collapseCancelsSystemNames?.has(primarySystem.name)) {
			onCancelEdit(primarySystem.name);
			return;
		}
		void handleRemoveSystem(primarySystem.name);
	};

	const handleToggleRow = (systemName: string) => {
		onToggleSystem(expandedSystem === systemName ? null : systemName);
	};

	const handleAddButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		if (!canEdit) return;
		if (pendingAddCloseAction || isCompletingPrimary) return;
		if (!isFilled && primarySystem) {
			onToggleSystem(isPrimaryFormOpen ? null : primarySystem.name);
			return;
		}
		if (addingSystem) {
			handleCancelForm(addingSystem.name);
			return;
		}
		onStartAdding();
	};

	const handleHeaderClick = () => {
		if (canEdit && !isHeaderFilled && !isPrimaryFormOpen && primarySystem) onToggleSystem(primarySystem.name);
	};

	const handleHeaderKeyDown = (event: KeyboardEvent) => {
		if (!canEdit || isHeaderFilled || isPrimaryFormOpen || !primarySystem) return;
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		onToggleSystem(primarySystem.name);
	};

	const handlePrimaryFormTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget || event.propertyName !== 'max-height') return;
		if (isPrimaryFormOpen) return;
		finishPendingPrimaryDone();
	};

	const handleAddFormTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') return;
		if (isAddFormOpen) return;
		finishPendingAddClose();
	};

	const getFormProps = (system: CriticalSystem, mode: FormMode) => ({
		system,
		onUpdateField,
		hasUnsavedChanges: (dirtySystemNames?.has(system.name) ?? false) && isSystemDocumented(system),
		isSaving: isSaving && savingSystemName === system.name,
		onSave: canEdit && onSaveSystem ? () => handleSaveForm(system.name) : undefined,
		onCancel: canEdit && (mode !== 'row' || onSaveSystem) ? () => handleCancelForm(system.name) : undefined,
		onDone: canEdit ? () => handleDoneForm(system.name) : undefined,
		onRemove:
			mode === 'row' && canRemove && onRemoveSystem && canRemoveSystem(system)
				? () => void handleRemoveSystem(system.name)
				: undefined,
		readOnly: !canEdit,
	});

	const rows = visibleSystems.map((system, index) => {
		return {
			system,
			rowLabel: getSystemRowLabel(system),
			notesSummary: getSystemNotesSummary(system),
			hasNotes: hasSystemNotes(system),
			isExpanded: expandedSystem === system.name,
			isLast: index === visibleSystems.length - 1 && !isAddFormOpen,
		};
	});
	const primaryRow = primarySystem
		? {
				rowLabel: getSystemRowLabel(primarySystem),
				notesSummary: getSystemNotesSummary(primarySystem),
				hasNotes: hasSystemNotes(primarySystem),
			}
		: null;

	return {
		primarySystem,
		addingSystem,
		rows,
		primaryRow,
		documentedCount,
		addFormRef,
		primaryFormRef,
		isCompletingPrimary,
		isHeaderFilled,
		isPrimaryFormOpen,
		isAddFormOpen,
		showPrimaryBlock,
		showEmptyHeaderLine,
		showPrimaryTooltipLine,
		headerSecondaryText: showPrimaryTooltipLine ? primarySystemInfo?.tooltip : 'None added',
		addFormHeading: addingSystem ? `Add ${categoryName} ${getSystemInstanceLabel(addingSystem.name)}` : undefined,
		showRemoveEmptySlot,
		handleAddButtonClick,
		handleHeaderClick,
		handleHeaderKeyDown,
		handleRemoveEmptySlot,
		handleToggleRow,
		handlePrimaryFormTransitionEnd,
		handleAddFormTransitionEnd,
		getFormProps,
	};
};
